import styles from './Terminals.module.scss';
import PropTypes from 'prop-types';
import OpeningHours from './OpeningHours';
import { useTranslation } from 'react-i18next';

const TerminalInfo = ({ terminalInfo }) => {
    const { t } = useTranslation();

    if (!terminalInfo) {
        return null;
    }

    const {
        name,
        address,
        zip,
        city,
        openingHours,
        openingHoursText,
        latitude,
        longitude,
        additionalText,
        facilityInfo: {
            dangerousGoodsAllowed,
            dangerousGoodsText,
            dangerousGoodsStorageAvailable,
            parkingPossible,
            parkingText,
            repairService,
            cleaning,
            reeferPlugsAvailable,
            customsOnSite,
            customsText,
        },
    } = terminalInfo;

    const notices = [
        {
            key: 'dangerousGoodsAllowed',
            label: t('terminals.dangerousGoodsAllowed'),
            value: `${dangerousGoodsAllowed}${dangerousGoodsText ? `, ${dangerousGoodsText}` : ''}`,
        },
        {
            key: 'dangerousGoodsStorageAvailable',
            label: t('terminals.dangerousGoodsStorageAvailable'),
            value: dangerousGoodsStorageAvailable,
        },
        {
            key: 'parkingPossible',
            label: t('terminals.parkingPossible'),
            value: `${parkingPossible}${parkingText ? `, ${parkingText}` : ''}`,
        },
        {
            key: 'repairService',
            label: t('terminals.repairService'),
            value: repairService,
        },
        {
            key: 'cleaning',
            label: t('terminals.cleaning'),
            value: cleaning,
        },
        {
            key: 'reeferPlugsAvailable',
            label: t('terminals.reeferPlugsAvailable'),
            value: reeferPlugsAvailable,
        },
        {
            key: 'customsOnSite',
            label: t('terminals.customsOnSite'),
            value: `${customsOnSite}${customsText ? `, ${customsText}` : ''}`,
        },
    ];

    return (
        <div className={styles.InfoSection}>
            <div className={styles.InfoSectionGroup}>
                {name && <p className={styles.InfoSectionName}>{name}</p>}
                {address && <p>{address}</p>}
                <p>{[zip, city].filter(Boolean).join(' ')}</p>
                {Boolean(latitude && longitude) && (
                    <div className={styles.MapLink}>
                        <p>
                            {t('terminals.coordinates')}: {latitude}° N, {longitude}° E
                        </p>
                        <a
                            href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                            target='_blank'
                        >
                            {t('terminals.openInGoogleMaps')}
                        </a>
                    </div>
                )}
            </div>

            <OpeningHours hours={openingHours} openingHoursText={openingHoursText} />

            <div className={styles.InfoSectionGroup}>
                <h3 className={styles.InfoSectionGroupHeader}>{t('terminals.notices')}</h3>
                <table className={styles.NoticesTable}>
                    {notices.map(
                        ({ key, label, value }) =>
                            value && (
                                <tr key={key}>
                                    <td>{label}:</td>
                                    <td>{value}</td>
                                </tr>
                            )
                    )}
                </table>
            </div>

            {additionalText && (
                <div className={styles.InfoSectionGroup}>
                    <h3 className={styles.InfoSectionGroupHeader}>
                        {t('terminals.furtherInformation')}
                    </h3>
                    <p>{additionalText}</p>
                </div>
            )}
        </div>
    );
};

TerminalInfo.propTypes = {
    terminalInfo: PropTypes.shape({
        name: PropTypes.string,
        address: PropTypes.string,
        zip: PropTypes.string,
        city: PropTypes.string,
        openingHours: PropTypes.arrayOf(
            PropTypes.shape({
                text: PropTypes.string,
                from: PropTypes.string,
                until: PropTypes.string,
            })
        ),
        latitude: PropTypes.string,
        longitude: PropTypes.string,
        openingHoursText: PropTypes.string,
        additionalText: PropTypes.string,
        facilityInfo: PropTypes.shape({
            dangerousGoodsAllowed: PropTypes.string,
            dangerousGoodsText: PropTypes.string,
            dangerousGoodsStorageAvailable: PropTypes.string,
            parkingPossible: PropTypes.string,
            parkingText: PropTypes.string,
            repairService: PropTypes.string,
            cleaning: PropTypes.string,
            reeferPlugsAvailable: PropTypes.string,
            customsOnSite: PropTypes.string,
            customsText: PropTypes.string,
        }),
    }).isRequired,
};

export default TerminalInfo;
